import type { FieldPolicy } from '@apollo/client';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isDate from 'lodash/isDate';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import { DateTime } from 'luxon';

import { getTimeZoneAwareDate, toISOMonth } from '../services/date';

import type { ScalarParsers as AdminScalarParsers } from './admin/scalarPaths';
import type { Maybe } from './user';
import type { ScalarParsers as UserScalarParsers } from './user/scalarPaths';

type ScalarFieldPolicy<IncomingType, ReturnType> = FieldPolicy<
  Maybe<ReturnType>,
  Maybe<IncomingType>
>;

export type ResponseTransform = (value: unknown) => unknown;

// Carried on field policies for scalarTransformLink, which converts minor-unit
// scalars to display units in every response before Apollo sees it — cache `read`
// fns can't do that consistently (they never run for mutation results or no-cache
// queries). Date policies stay merge-based in the cache instead: date fields
// participate in keyFields, which are computed from the raw incoming strings, so
// converting dates pre-cache would change entity ids.
export const responseTransform = Symbol('responseTransform');

type TransformedScalarPolicy<IncomingType, ReturnType> = ScalarFieldPolicy<IncomingType, ReturnType>
& Readonly<Record<typeof responseTransform, ResponseTransform>>;

const toMajorUnits: ResponseTransform = (value) => (isNumber(value) ? value / 100 : value);

export const monthDayPolicy: ScalarFieldPolicy<Date | string, Date> = {
  merge: (_, incoming) => {
    if (isNil(incoming) || isDate(incoming)) {
      return incoming;
    }

    // MonthDay in format --MM-DD
    const monthDayRegex = /^--[0-9]{2}-[0-9]{2}$/;

    if (isString(incoming) && monthDayRegex.exec(incoming)) {
      return new Date(`1970-${incoming.slice(2)}`);
    }

    return null;
  },
};

type ArrayScalarFieldPolicy = ScalarFieldPolicy<unknown[], unknown[]>
& Partial<Record<typeof responseTransform, ResponseTransform>>;

export const arrayTypePolicy = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ofType: ScalarFieldPolicy<any, any> & Partial<Record<typeof responseTransform, ResponseTransform>>,
): ArrayScalarFieldPolicy => {
  const elementTransform = ofType[responseTransform];

  return {
    ...(isNil(elementTransform) ? {} : {
      [responseTransform]: (value: unknown) => (isArray(value) ? value.map(elementTransform) : value),
    }),
    merge: (_, incoming, opts) => {
      const fn = ofType.merge;

      if (isNil(fn) || isBoolean(fn) || !isArray(incoming)) {
        return incoming;
      }

      return incoming.map((data): unknown => fn(_, data, opts));
    },
  };
};

export const dateTypePolicy: ScalarFieldPolicy<Date | string, Date> = {
  merge: (_, incoming) => {
    if (isNil(incoming)) {
      return incoming;
    }

    if (isString(incoming)) {
      return DateTime.fromISO(incoming).toJSDate();
    }

    return incoming;
  },
};

export const localDateTypePolicy: ScalarFieldPolicy<Date | string, Date> = {
  merge: (_, incoming) => {
    if (isNil(incoming)) {
      return incoming;
    }

    if (isString(incoming)) {
      return getTimeZoneAwareDate(incoming);
    }

    return incoming;
  },
};

export const timeTypePolicy: ScalarFieldPolicy<Date | string, Date> = {
  merge: (_, incoming) => {
    if (isNil(incoming)) {
      return incoming;
    }

    if (isString(incoming)) {
      return DateTime.fromISO(incoming).toJSDate();
    }

    return incoming;
  },
};

export const basisPointTypePolicy: TransformedScalarPolicy<number, number> = {
  [responseTransform]: toMajorUnits,
};

export const centsTypePolicy: TransformedScalarPolicy<number, number> = {
  [responseTransform]: toMajorUnits,
};

type ScalarVatStringValues = 'ExemptFromVAT' | 'VatNotApplicable';
type ScalarVat = ScalarVatStringValues | number;
export const vatTypeInputPolicy: TransformedScalarPolicy<ScalarVat, ScalarVat> = {
  [responseTransform]: toMajorUnits,
};

export const scalarParsers: AdminScalarParsers & UserScalarParsers = {
  Cents: (number) => {
    if (isNil(number)) {
      return null;
    }

    return Math.round(number * 100);
  },
  DateTime: (date) => {
    if (isNil(date)) {
      return null;
    }

    return DateTime.fromJSDate(date).toISO();
  },
  LocalDate: (date) => {
    if (isNil(date)) {
      return null;
    }

    return DateTime.fromJSDate(date).toISODate();
  },
  LocalTime: (date) => {
    if (isNil(date)) {
      return null;
    }

    return DateTime.fromJSDate(date).toLocaleString(DateTime.TIME_24_SIMPLE);
  },
  BasisPoint: (basisPoint) => {
    if (isNil(basisPoint)) {
      return null;
    }

    return Math.round(basisPoint * 100);
  },
  YearMonth: (date) => {
    if (isNil(date)) {
      return null;
    }

    return toISOMonth(date);
  },
  Date: (date) => {
    if (isNil(date)) {
      return null;
    }

    return DateTime.fromJSDate(date).toISODate();
  },
  OffsetDateTime: (date) => {
    if (isNil(date)) {
      return null;
    }

    return DateTime.fromJSDate(date).toISO({ includeOffset: true });
  },
  LocalDateTime: (date) => {
    if (isNil(date)) {
      return null;
    }

    return DateTime.fromJSDate(date).toISO();
  },
  VatTypeInput: (vatValue) => {
    if (isNil(vatValue) || isString(vatValue)) {
      return vatValue;
    }

    return vatValue * 100;
  },
};
